"use client";

import React, { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";

interface User {
  username: string;
  token: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  // הוספנו את ה-code כפרמטר אופציונלי
  login: (username: string, password: string, code?: string) => Promise<boolean>;
  register: (formData: any) => Promise<{ success: boolean; message: string }>;
  logout: () => void;
  isLoading: boolean;
  authFetch: (url: string, options?: RequestInit) => Promise<Response>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const API_BASE_URL = "https://api.tuviai.org";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const logout = useCallback(() => {
    console.warn("[פקד אמנון]: מבצע פינוי ציר - התנתקות.");
    localStorage.removeItem("token");
    localStorage.removeItem("username");
    localStorage.removeItem("role");
    setUser(null);
    if (typeof window !== "undefined" && window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
  }, []);

  const authFetch = useCallback(async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem("token");
    const headers: Record<string, string> = { ...(options.headers as Record<string, string>) };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    if (options.body instanceof FormData) {
      delete headers["Content-Type"];
    } else if (!headers["Content-Type"]) {
      headers["Content-Type"] = "application/json";
    }

    const response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
      logout();
      throw new Error("Unauthorized");
    }
    return response;
  }, [logout]);

  // --- לוגין מעודכן עם תמיכה ב-2FA ---
  const login = useCallback(async (username: string, password: string, code?: string): Promise<boolean> => {
    console.log("[פקד אמנון]: ניסיון כניסה סופי עבור:", username, code ? "עם קוד 2FA" : "ללא קוד");
    setIsLoading(true);

    try {
      const formData = new URLSearchParams();
      formData.append("username", username);
      formData.append("password", password);
      if (code) {
        formData.append("code", code); // שליחת הקוד ל-Backend
      }

      const response = await fetch(`${API_BASE_URL}/token`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData,
      });

      if (!response.ok) {
        setIsLoading(false);
        return false;
      }

      const data = await response.json();

      if (!data.access_token) {
        setIsLoading(false);
        return false;
      }

      localStorage.setItem("token", data.access_token);
      localStorage.setItem("username", username);
      localStorage.setItem("role", data.role || "User");

      setUser({
        username,
        token: data.access_token,
        role: data.role || "User"
      });

      setIsLoading(false);
      return true;
    } catch (error) {
      console.error("[פקד אמנון]: תקלה ב-Login:", error);
      setIsLoading(false);
      return false;
    }
  }, []);

  const register = useCallback(async (formData: any) => {
    try {
      const response = await fetch(`${API_BASE_URL}/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      return response.ok
        ? { success: true, message: "הבקשה נשלחה לאישור המנהל" }
        : { success: false, message: data.detail || "תקלה ברישום" };
    } catch (error) {
      return { success: false, message: "אין תקשורת עם השרת" };
    }
  }, []);

  useEffect(() => {
    const savedToken = localStorage.getItem("token");
    const savedUsername = localStorage.getItem("username");
    const savedRole = localStorage.getItem("role");

    if (savedToken && savedUsername && savedRole) {
      try {
        const payload = JSON.parse(atob(savedToken.split('.')[1]));
        if (payload.exp < Date.now() / 1000) {
          logout();
        } else {
          setUser({ username: savedUsername, token: savedToken, role: savedRole });
        }
      } catch (e) {
        logout();
      }
    }
    setIsLoading(false);
  }, [logout]);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, login, register, logout, isLoading, authFetch }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};